<template>
  <div class="top-navbar" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
    <div class="navbar-content">
      <div class="navbar-left">
        <button
          v-if="isSidebarCollapsed"
          type="button"
          class="nav-expand"
          title="Ouvrir le menu"
          @click="$emit('toggle-sidebar')"
        >
          <i class="ri-side-bar-line"></i>
        </button>

        <div class="navbar-heading">
          <nav class="breadcrumb" aria-label="Fil d'ariane">
            <span>{{ breadcrumbRoot }}</span>
            <span class="breadcrumb__sep">/</span>
            <span class="breadcrumb__current">{{ breadcrumbSection }}</span>
          </nav>
          <h1 class="page-title">{{ pageTitle }}</h1>
        </div>
      </div>

      <div class="right-section">
        <div v-if="showSurveyLinks" class="nav-links">
          <router-link
            :to="{ name: 'my-surveys' }"
            class="nav-link"
            :class="{ active: $route.name === 'my-surveys' }"
          >
            Liste des enquêtes
          </router-link>
          <router-link
            :to="{ name: 'Survey' }"
            class="nav-link"
            :class="{ active: $route.name === 'Survey' }"
          >
            Ajouter enquête
          </router-link>
        </div>

        <div v-click-outside="closeMenu" class="user-menu">
          <button
            type="button"
            class="user-section"
            :aria-expanded="menuOpen"
            aria-haspopup="true"
            @click="menuOpen = !menuOpen"
          >
            <span class="user-avatar">{{ userInitials }}</span>
            <span class="user-identity">
              <span class="user-name">{{ userName }}</span>
              <span class="user-role">{{ roleLabel }}</span>
            </span>
            <i class="ri-arrow-down-s-line user-caret"></i>
          </button>

          <div v-if="menuOpen" class="user-dropdown">
            <button
              type="button"
              class="user-dropdown__item user-dropdown__item--danger"
              @click="logout"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { initials } from '@/shared/utils/user-display';

export default {
  name: 'TopNavbar',
  emits: ['logout', 'toggle-sidebar'],
  props: {
    pageTitle: {
      type: String,
      default: 'Tableau de Bord Global',
    },
    userName: {
      type: String,
      default: '',
    },
    isSidebarCollapsed: {
      type: Boolean,
      default: false,
    },
    /** Second half of the breadcrumb; the first is always the space name. */
    breadcrumbSection: {
      type: String,
      default: '',
    },
    roleLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return { menuOpen: false };
  },
  computed: {
    breadcrumbRoot() {
      return 'Administration';
    },

    showSurveyLinks() {
      // Afficher les liens seulement sur les pages d'enquêtes professionnelles (pas admin)
      return (
        this.$route.name === 'my-surveys' ||
        this.$route.name === 'Survey' ||
        this.$route.name === 'survey-edit'
      );
    },
    userInitials() {
      return initials({ name: this.userName });
    },
  },
  watch: {
    // A menu left open across a navigation would hang over the next screen.
    $route() {
      this.menuOpen = false;
    },
  },
  methods: {
    closeMenu() {
      this.menuOpen = false;
    },

    logout() {
      this.menuOpen = false;
      this.$emit('logout');
    },
  },
};
</script>

<style scoped>
/* Admin bar — Claude Design: 72px, breadcrumb over title, user chip. */
.top-navbar {
  position: fixed;
  top: 0;
  left: 248px;
  right: 0;
  height: 72px;
  background: #fff;
  border-bottom: 1px solid #e5e9ef;
  z-index: 5;
  transition: left 0.18s ease;
}

.top-navbar.sidebar-collapsed {
  left: 76px;
}

.navbar-content {
  height: 100%;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.nav-expand {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e5e9ef;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.nav-expand:hover {
  background: #f4f7fb;
  border-color: #cfd6e0;
}

.navbar-heading {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #667085;
  margin: 0;
  padding: 0;
}

.breadcrumb__sep {
  color: #c7cdd6;
}

.breadcrumb__current {
  color: #475467;
}

.page-title {
  font-size: 19px;
  font-weight: 600;
  color: #101828;
  letter-spacing: -0.2px;
  margin: 0;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 14px;
}

.nav-links {
  display: flex;
  gap: 20px;
}

.nav-link {
  font-size: 13px;
  font-weight: 500;
  color: #475467;
  text-decoration: none;
}

.nav-link.active {
  color: #005bd0;
}

/* The user chip: a bordered pill, not a bare avatar. */
.user-menu {
  position: relative;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px 5px 5px;
  border: 1px solid #e5e9ef;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
}

.user-section:hover {
  background: #f4f7fb;
  border-color: #cfd6e0;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #005bd0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.user-identity {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  text-align: left;
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  color: #101828;
}

.user-role {
  font-size: 11px;
  color: #667085;
}

.user-caret {
  font-size: 16px;
  color: #667085;
  line-height: 1;
  margin-left: 2px;
}

.user-dropdown {
  position: absolute;
  right: 0;
  top: 50px;
  width: 220px;
  background: #fff;
  border: 1px solid #e9ebec;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.14);
  padding: 6px;
  z-index: 30;
  display: flex;
  flex-direction: column;
}

.user-dropdown__item {
  padding: 10px 12px;
  border: 0;
  border-radius: 6px;
  background: none;
  font-size: 13px;
  color: #374151;
  text-align: left;
  cursor: pointer;
}

.user-dropdown__item:hover {
  background: #f3f4f6;
}

.user-dropdown__item--danger {
  color: #dc3545;
}

.user-dropdown__item--danger:hover {
  background: #fdeaec;
}

/* Tablette: le rail garde sa largeur, la barre suit; seul le padding cede. */
@media (max-width: 991px) {
  .navbar-content {
    padding: 0 18px;
  }
}

/*
  Telephone. La barre reprend toute la largeur - le rail est passe en tiroir -
  et son contenu se serre:
  - le fil d'ariane garde la section courante et laisse tomber « Administration »,
    qui ne dit rien de plus que le rail deja ouvert d'un doigt;
  - le titre s'abrege plutot que de disparaitre: a 142px de large, il tombait a
    zero et laissait la barre muette;
  - la pastille se reduit aux initiales, le nom et le role restant lisibles dans
    le menu qu'elle ouvre.
*/
@media (max-width: 767px) {
  .top-navbar,
  .top-navbar.sidebar-collapsed {
    left: 0;
  }

  .navbar-content {
    padding: 0 12px;
    gap: 10px;
  }

  .navbar-heading {
    min-width: 0;
  }

  .breadcrumb {
    font-size: 10px;
  }

  .breadcrumb span:not(.breadcrumb__current) {
    display: none;
  }

  .page-title {
    font-size: 15px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-identity {
    display: none;
  }

  .user-section {
    padding: 4px;
    gap: 0;
  }

  .user-caret {
    display: none;
  }

  .user-dropdown {
    right: 0;
  }
}
</style>
